import React from "react"
import {
  ActionType,
  BrandLogoBlock,
  NavTab,
  SideNavFrame,
  useSideNavCollapse,
} from "../side-nav"

// Side navigation glyphs from the design system (currentColor).
import { ReactComponent as AssetsIcon } from "assets/svg/side-nav/assets.svg"
import { ReactComponent as DashboardIcon } from "assets/svg/side-nav/dashboard.svg"
import { ReactComponent as DisciplinaryIcon } from "assets/svg/side-nav/disciplinary.svg"
import { ReactComponent as DocumentsIcon } from "assets/svg/side-nav/documents.svg"
import { ReactComponent as EmployeeIcon } from "assets/svg/side-nav/employee.svg"
import { ReactComponent as PerformanceIcon } from "assets/svg/side-nav/performance.svg"
import { ReactComponent as RequestIcon } from "assets/svg/side-nav/request.svg"
import { ReactComponent as SurveyIcon } from "assets/svg/side-nav/survey.svg"
import { ReactComponent as TaskIcon } from "assets/svg/side-nav/task.svg"
import { ReactComponent as TimeAndAttendanceIcon } from "assets/svg/side-nav/time-and-attendance.svg"
import { ReactComponent as WalletIcon } from "assets/svg/side-nav/wallet.svg"
import {
  CurrencyDollarIcon,
  DocumentIcon,
  GiftIcon,
  UserCircleIcon,
  CreditCardIcon,
  ClipboardDocumentListIcon,
  BuildingLibraryIcon,
} from "@heroicons/react/24/outline"

import { Squares2X2Icon } from "@heroicons/react/24/solid"
import { useAuthStore } from "stores"
import { isLocalOrDevOrStaging, isPadeDomain } from "utils/utils"
import { useCustomizationSettingsStore } from "stores/customization-settings"
import PadeLogoWhite from "assets/logos/paidhr-logo-white.svg"
import { useGet } from "utils/useFetch"
import { ITracker } from "pages/dashboard/performance/types"
import { devUrl, localUrl, stagingUrl } from "utils/constants"

export const SideNavigationBar: React.FC<{
  isSidebarOpen: boolean
  toggleSidebar: React.Dispatch<React.SetStateAction<boolean>>
}> = ({ isSidebarOpen, toggleSidebar }) => {
  const { compact } = useSideNavCollapse()
  const { user } = useAuthStore()
  const { customizationSettings } = useCustomizationSettingsStore()
  const { newPerformanceRoutes } = useNetworkEnabledRoutes()
  const origin = window.location.origin

  const show = origin === stagingUrl || origin === devUrl || origin === localUrl

  const canAccessTandA =
    ["22", "837"].includes(String(user?.userOrganisationId ?? "")) ||
    user?.timeTrackingEnabled === true

  const canAccessOldPerformance =
    ["235"].includes(String(user?.userOrganisationId ?? "")) ||
    isLocalOrDevOrStaging()

  const actions: ActionType[] = [
    {
      title: "People",
      icon: <EmployeeIcon className="w-4" />,
      route: "people",
      patterns: [{ path: "/employee/dashboard/people/*" }],
      enabled: isPadeDomain() ? true : user?.coworkerDirectoryEnabled === true,
      badge: false,
    },
    {
      title: "Job & Pay",
      icon: <CurrencyDollarIcon className="w-4" />,
      route: "job-and-pay",
      patterns: [{ path: "/employee/dashboard/job-and-pay/*" }],
      enabled: !user?.isPayrollEnabled ? false : true,
      badge: false,
    },
    {
      title: "Personal",
      icon: <UserCircleIcon className="w-4" />,
      route: "personal",
      patterns: [{ path: "/employee/dashboard/personal/*" }],
      enabled: true,
      badge: false,
    },
    {
      title: "Org. Chart",
      icon: <BuildingLibraryIcon className="w-4" />,
      route: "org-chart",
      patterns: [{ path: "/employee/dashboard/org-chart/*" }],
      enabled: user?.coworkerDirectoryEnabled === true,
      badge: false,
    },
    {
      title: "Requests",
      icon: <RequestIcon className="w-4" />,
      patterns: [{ path: "/employee/dashboard/requests/*" }],
      enabled: !user?.isHrisEnabled ? false : true,
      badge: false,
      // One entry per kind of request, each its own page, rather than an "All requests" page
      // with a second sidebar inside this one. Create forms keep their existing addresses, so
      // their patterns are listed with the page they belong to.
      subActions: [
        {
          title: "Leave",
          route: "requests/leave",
          patterns: [
            { path: "/employee/dashboard/requests/leave" },
            { path: "/employee/dashboard/requests/all/create-leave" },
            { path: "/employee/dashboard/requests/leave-balances" },
          ],
          enabled: true,
        },
        {
          title: "Loans",
          route: "requests/loans",
          patterns: [
            { path: "/employee/dashboard/requests/loans" },
            { path: "/employee/dashboard/requests/all/create-loan" },
          ],
          enabled: true,
        },
        {
          title: "Salary advances",
          route: "requests/salary-advances",
          patterns: [
            { path: "/employee/dashboard/requests/salary-advances" },
            { path: "/employee/dashboard/requests/all/create-salaryadvance" },
          ],
          enabled: true,
        },
        {
          title: "Documents",
          route: "requests/documents",
          patterns: [
            { path: "/employee/dashboard/requests/documents" },
            { path: "/employee/dashboard/requests/all/create-document" },
          ],
          enabled: true,
        },
        {
          title: "Exits",
          route: "requests/exits",
          patterns: [
            { path: "/employee/dashboard/requests/exits" },
            { path: "/employee/dashboard/requests/all/create-exit" },
          ],
          enabled: true,
        },
        {
          title: "Redeployments",
          route: "requests/redeployments",
          patterns: [
            { path: "/employee/dashboard/requests/redeployments" },
            { path: "/employee/dashboard/requests/all/create-redeployment" },
          ],
          enabled: true,
        },
        {
          title: "Requisitions",
          route: "requests/requisitions",
          patterns: [{ path: "/employee/dashboard/requests/requisitions/*" }],
          enabled: true,
          badge: "New",
        },
      ],
    },
    // {
    //   title: "Requests",
    //   icon: <UsersIcon className="w-5" />,
    //   subActions: [
    //     {
    //       title: "Leaves",
    //       route: "request/leaves",
    //       patterns: [{ path: "/employee/dashboard/request/leaves/*" }],
    //     },
    //     {
    //       title: "Loan",
    //       route: "request/loan",
    //       patterns: [{ path: "/employee/dashboard/request/loan/*" }],
    //     },
    //     {
    //       title: "Salary Advance",
    //       route: "request/salary-advance",
    //       patterns: [{ path: "/employee/dashboard/request/salary-advance/*" }],
    //     },
    //     {
    //       title: "Documents",
    //       route: "request/documents",
    //       patterns: [{ path: "/employee/dashboard/request/documents/*" }],
    //     },
    //     {
    //       title: "Exit",
    //       route: "request/exit",
    //       patterns: [{ path: "/employee/dashboard/request/exit/*" }],
    //     },
    //   ],
    // },
    {
      title: "Documents",
      icon: <DocumentsIcon className="w-4" />,
      route: "document",
      patterns: [{ path: "/employee/dashboard/document/*" }],
      enabled: !user?.isHrisEnabled ? false : true,
      badge: false,
    },
    {
      title: "Surveys",
      icon: <SurveyIcon className="w-4" />,
      route: "survey",
      patterns: [{ path: "/employee/dashboard/survey/*" }],
      enabled: true,
      // enabled: false,
      badge: false,

      // enabled: false,
    },
    {
      title: "Benefits",
      icon: <GiftIcon className="w-4" />,
      route: "benefits",
      patterns: [{ path: "/employee/dashboard/benefits/*" }],
      enabled: !user?.isHrisEnabled ? false : true,
      badge: false,
    },
    {
      title: "Payables",
      icon: <CreditCardIcon className="w-4" />,
      route: "payables",
      patterns: [{ path: "/employee/dashboard/payables/*" }],
      enabled: !user?.isPayrollEnabled ? false : true,
      badge: false,
    },
    {
      title: "Payslips",
      icon: <ClipboardDocumentListIcon className="w-4" />,
      route: "payslip",
      patterns: [{ path: "/employee/dashboard/payslip/*" }],
      enabled: !user?.isPayrollEnabled ? false : true,
      badge: false,
    },
    {
      title: "Wallet",
      icon: <WalletIcon className="w-4" />,
      enabled: user?.userOrganisationId === 243 ? false : true,
      badge: false,
      subActions: [
        {
          title: "Overview",
          route: "wallet/cross-border/overview",
          patterns: [
            {
              path: "/employee/dashboard/wallet/cross-border/overview/*",
            },
            {
              path: "/employee/dashboard/wallet/cross-border/local/*",
            },
            {
              path: "/employee/dashboard/wallet/cross-border/view/*",
            },
            {
              path: "/employee/dashboard/wallet/cross-border/swap-money/*",
            },
            {
              path: "/employee/dashboard/wallet/cross-border/send-money/*",
            },
            {
              path: "/employee/dashboard/wallet/cross-border/move-funds/*",
            },
          ],
          enabled: user?.userOrganisationId === 108 ? false : true,
        },
        {
          title: "Overview",
          route: "wallet/cross-border/local",
          patterns: [
            {
              path: "/employee/dashboard/wallet/cross-border/local/*",
            },
          ],
          enabled: user?.userOrganisationId === 108 ? true : false,
        },

        {
          title: "Transactions",
          route: "wallet/cross-border/transactions",
          patterns: [
            {
              path: "/employee/dashboard/wallet/cross-border/transactions/*",
            },
          ],
          enabled: true,
        },
        {
          title: "Transfers",
          route: "wallet/cross-border/transfers",
          patterns: [
            {
              path: "/employee/dashboard/wallet/cross-border/transfers/*",
            },
          ],
          enabled: true,
        },
        {
          title: "Earned Wage Access",
          route: "wallet/earned-wage-access/overview",
          patterns: [
            {
              path: "/employee/dashboard/wallet/earned-wage-access/overview/*",
            },
            {
              path: "employee/dashboard/wallet/earned-wage-access/transactions/*",
            },
          ],
          enabled: true,
          // enabled: user?.isEwaEnabled === true,
        },
        {
          title: "Bill Payments",
          route: "wallet/bill-payments",
          patterns: [{ path: "/employee/dashboard/wallet/bill-payments/*" }],
          enabled: true,
        },
      ],
    },
    // {
    //   title: "Christmas Village",
    //   icon: <GiftIcon className="w-5" />,
    //   route: "christmas-village",
    //   patterns: [{ path: "/employee/dashboard/christmas-village/*" }],
    //   enabled: true,
    //   badge: false,
    // },

    {
      title: "Assets",
      icon: <AssetsIcon className="w-4" />,
      route: "assets",
      patterns: [{ path: "/employee/dashboard/assets/*" }],
      enabled: !user?.isHrisEnabled ? false : true,
      badge: false,
    },
    {
      title: "Tasks",
      icon: <TaskIcon className="w-4" />,
      route: "tasks",
      patterns: [{ path: "/employee/dashboard/tasks/*" }],
      enabled: !user?.isPerformanceEnabled ? false : true,
      badge: false,
    },

    ...newPerformanceRoutes,
    {
      title: "Performance",
      icon: <PerformanceIcon className="w-4" />,
      route: "performance",
      patterns: [{ path: "/employee/dashboard/performance/*" }],
      enabled: user?.isPerformanceEnabled === true && canAccessOldPerformance,
      badge: "Legacy" as const,
      subActions: [
        {
          title: "Overview",
          route: "overview",
          patterns: [
            {
              path: "/employee/dashboard/overview/*",
            },
          ],
          enabled: !user?.isPerformanceEnabled ? false : true,
        },
        {
          title: "Objectives",
          route: "objectives",
          patterns: [
            {
              path: "/employee/dashboard/objectives/*",
            },
          ],
          enabled: !user?.isPerformanceEnabled ? false : true,
        },
        {
          title: "Reviews",
          route: "reviews ",
          patterns: [{ path: "/employee/dashboard/reviews/*" }],
          enabled: !user?.isPerformanceEnabled ? false : true,
        },
        {
          title: "Conversations",
          route: "conversations",
          patterns: [
            { path: "/employee/dashboard/conversations/*" },
            { path: "/employee/dashboard/add-prompt/*" },
          ],
          enabled: true,
        },
        {
          title: "Feedback",
          route: "feedback",
          patterns: [{ path: "/employee/dashboard/feedback/*" }],
          enabled: true,
        },
      ],
    },

    {
      title: "Time & Attendance",
      icon: <TimeAndAttendanceIcon className="w-4" />,
      route: "time-and-attendance",
      patterns: [{ path: "/employee/dashboard/time-and-attendance/*" }],
      enabled: canAccessTandA,
      badge: "New" as const,
    },
    {
      title: "Disciplinary",
      icon: <DisciplinaryIcon className="w-4" />,
      route: "disciplinary",
      patterns: [{ path: "/employee/dashboard/disciplinary/*" }],
      enabled: !user?.isHrisEnabled ? false : true,
      badge: false,
    },
    /*{
      title: "Notes",
      icon: <DocumentIcon className="w-4" />,
      route: "notes",
      patterns: [{ path: "/employee/dashboard/notes/*" }],
      enabled: !user?.isHrisEnabled ? false: true,
    },*/
  ]

  const handleToggleSidebar = () => {
    toggleSidebar(false)
    document.body.classList.remove("overflow-hidden")
  }

  return (
    <>
      {isSidebarOpen && (
        <div
          className="md:hidden fixed inset-0 z-20 bg-black/30  cursor-pointer backdrop-blur-sm"
          onClick={handleToggleSidebar}
        />
      )}
      <SideNavFrame
        isSidebarOpen={isSidebarOpen}
        compact={compact}
        zIndexClass="z-30"
        backdrop
      >
        <div className="h-full w-full  px-3 flex flex-col justify-start gap-3 relative z-[2]">
          {/* Brand */}
          <BrandLogoBlock
            compact={compact}
            orgName={user?.userOrganisation}
            alt="PaidHR Logo"
            src={
              isPadeDomain()
                ? PadeLogoWhite
                : customizationSettings?.brand?.menuLogoUrl
            }
          />
          {/* Menu */}
          <div className="h-full w-full flex flex-col gap-9">
            {/* Dashboard */}
            <div className="gap-0.5 flex flex-col">
              {user?.userStatusId === 5 ? (
                <NavTab
                  action={{
                    title: "Onboarding",
                    route: "welcome",
                    icon: <Squares2X2Icon className="w-4" />,
                    patterns: [{ path: "employee/dashboard/welcome" }],
                    enabled: true,
                    badge: false,
                  }}
                  toggleSidebar={handleToggleSidebar}
                  compact={compact}
                />
              ) : (
                ""
              )}

              {user?.userStatusId === 5 ? (
                ""
              ) : (
                <NavTab
                  action={{
                    title: "Dashboard",
                    route: "",
                    icon: <DashboardIcon className="w-4" />,
                    patterns: [{ path: "employee/dashboard" }],
                    enabled: true,
                    badge: false,
                  }}
                  toggleSidebar={handleToggleSidebar}
                  compact={compact}
                />
              )}
            </div>
            {
              /* Other Menu */
              user?.userStatusId === 5 ? (
                ""
              ) : (
                <div className="h-full w-full flex flex-col">
                  <div className="h-full w-full flex flex-col">
                    <div className="pb-20 w-full flex flex-col gap-0.5">
                      {actions.map((action, index) =>
                        !action.enabled ? (
                          ""
                        ) : (
                          <NavTab
                            action={action}
                            key={index}
                            index={index}
                            toggleSidebar={handleToggleSidebar}
                            compact={compact}
                          />
                        )
                      )}
                    </div>
                  </div>
                </div>
              )
            }
          </div>
        </div>
      </SideNavFrame>
    </>
  )
}

const useNetworkEnabledRoutes = () => {
  const user = useAuthStore((state) => state.user)
  const { isLoadingPerformanceSetupProgress, isPerformanceSetupComplete } =
    useAuthStore((state) => ({
      isLoadingPerformanceSetupProgress:
        state.user?.isLoadingPerformanceTracker,
      isPerformanceSetupComplete: state.user?.isNewPerformanceSetupCompleted,
    }))

  const newPerformanceRoutes =
    isLoadingPerformanceSetupProgress || !isPerformanceSetupComplete
      ? []
      : [
          {
            title: "Performance",
            icon: <PerformanceIcon className="w-4" />,
            route: "performance",
            enabled: user?.isPerformanceEnabled === true,
            // enabled:isLocalOrDevOrStaging() || user?.userOrganisationId === 22,
            badge: false as ActionType["badge"],
            subActions: [
              {
                title: "Overview",
                route: "performance/overview",
                patterns: [
                  { path: "/employee/dashboard/performance/overview/*" },
                ],
                enabled: true,
              },
              {
                title: "OKRs / KPIs",
                route: "performance/okr",
                patterns: [{ path: "/employee/dashboard/performance/okr/*" }],
                enabled: true,
              },
              {
                title: "Reviews",
                route: "performance/reviews",
                patterns: [
                  { path: "/employee/dashboard/performance/reviews/*" },
                ],
                enabled: true,
              },
            ],
          },
        ]
  return { newPerformanceRoutes }
}
